/*
  HUSTLESTREET — Cart drawer logic (PHASE 9)
  File: assets/hs-cart.js
  Loaded by: sections/hs-cart-drawer.liquid

  Integrations (no changes needed in other files):
    - Forms with [data-hs-quick-add] (product cards) or [data-hs-product-form] (product page, PHASE 11)
      are submitted via AJAX and open the drawer.
    - Any link to the cart page, or any element with [data-hs-cart-open], opens the drawer.
    - Header cart links are refreshed through the Section Rendering API (count stays in sync).
    - Elements with [data-hs-cart-count] receive the item count.
    - Events on document: 'hs:cart:updated' { itemCount }, 'hs:cart:added' { item }.
    - Public API: window.HSCart.open(), .close(), .refresh(), .add(formData)
*/
(function () {
  if (!('customElements' in window) || customElements.get('hs-cart-drawer')) return;

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class HsCartDrawer extends HTMLElement {
    connectedCallback() {
      const firstDrawer = document.querySelector('hs-cart-drawer');
      this.inactive = firstDrawer !== this;
      if (this.inactive) return;

      this.sectionId = this.dataset.sectionId;
      this.cartUrl = this.dataset.cartUrl || '/cart';
      this.addUrl = this.dataset.cartAddUrl || '/cart/add';
      this.changeUrl = this.dataset.cartChangeUrl || '/cart/change';
      this.openOnAdd = this.dataset.openOnAdd !== 'false';
      this.cartPath = this.normalizePath(new URL(this.cartUrl, window.location.origin).pathname);

      this.panel = this.querySelector('[data-hs-cart-panel]');
      this.live = this.querySelector('[data-hs-cart-live]');
      this.errorBox = this.querySelector('[data-hs-cart-error]');
      this.busy = false;

      this.onDocClick = this.onDocClick.bind(this);
      this.onDocSubmit = this.onDocSubmit.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onSectionSelect = this.onSectionSelect.bind(this);
      this.onSectionDeselect = this.onSectionDeselect.bind(this);
      this.onPageShow = this.onPageShow.bind(this);

      document.addEventListener('click', this.onDocClick);
      document.addEventListener('submit', this.onDocSubmit);
      document.addEventListener('shopify:section:select', this.onSectionSelect);
      document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
      window.addEventListener('pageshow', this.onPageShow);
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);

      window.HSCart = {
        _el: this,
        open: () => this.open(),
        close: () => this.close(),
        refresh: () => this.refresh(),
        add: (formData) => this.add(formData)
      };
    }

    disconnectedCallback() {
      if (this.inactive) return;

      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('submit', this.onDocSubmit);
      document.removeEventListener('shopify:section:select', this.onSectionSelect);
      document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
      window.removeEventListener('pageshow', this.onPageShow);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);

      document.documentElement.classList.remove('hs-cart-is-open');

      if (window.HSCart && window.HSCart._el === this) {
        delete window.HSCart;
      }
    }

    /* ---------- Helpers ---------- */

    normalizePath(path) {
      return path.length > 1 ? path.replace(/\/$/, '') : path;
    }

    isCartLink(link) {
      const href = link.getAttribute('href');
      if (!href || href.charAt(0) === '#' || href.indexOf('mailto:') === 0 || href.indexOf('tel:') === 0) {
        return false;
      }
      try {
        const url = new URL(href, window.location.origin);
        return url.origin === window.location.origin && this.normalizePath(url.pathname) === this.cartPath;
      } catch (error) {
        return false;
      }
    }

    getCartLinks(root) {
      return Array.from(root.querySelectorAll('a[href]')).filter(
        (link) => this.isCartLink(link) && !this.contains(link)
      );
    }

    getSectionIds() {
      const ids = [this.sectionId];
      this.getCartLinks(document).forEach((link) => {
        const section = link.closest('.shopify-section');
        if (section && section.id.indexOf('shopify-section-') === 0) {
          const id = section.id.replace('shopify-section-', '');
          if (ids.indexOf(id) === -1) ids.push(id);
        }
      });
      return ids.slice(0, 5);
    }

    /* ---------- Events ---------- */

    onDocClick(event) {
      if (event.defaultPrevented) return;
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const opener = event.target.closest('[data-hs-cart-open]');
      if (opener && !this.contains(opener)) {
        event.preventDefault();
        this.open(opener);
        return;
      }

      const link = event.target.closest('a[href]');
      if (!link || this.contains(link) || !this.isCartLink(link)) return;
      if (this.normalizePath(window.location.pathname) === this.cartPath) return;

      event.preventDefault();
      this.open(link);
    }

    onDocSubmit(event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.matches('[data-hs-quick-add], [data-hs-product-form]')) return;

      event.preventDefault();

      const formData = new FormData(form);
      const submitter = event.submitter || form.querySelector('[type="submit"]');
      if (submitter && submitter.name) {
        formData.set(submitter.name, submitter.value);
      }

      this.add(formData, submitter);
    }

    onClick(event) {
      const closeButton = event.target.closest('[data-hs-cart-close]');
      if (closeButton) {
        event.preventDefault();
        this.close();
        return;
      }

      const qtyButton = event.target.closest('[data-hs-cart-qty]');
      if (qtyButton) {
        event.preventDefault();
        const quantity = parseInt(qtyButton.dataset.qty, 10);
        if (!isNaN(quantity) && quantity >= 0) {
          this.change(qtyButton.dataset.key, quantity);
        }
      }
    }

    onKeydown(event) {
      if (!this.classList.contains('is-open')) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;

      const items = Array.from(this.panel.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement
      );

      if (!items.length) {
        event.preventDefault();
        this.panel.focus();
        return;
      }

      const first = items[0];
      const last = items[items.length - 1];

      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onSectionSelect(event) {
      if (event.detail && event.detail.sectionId === this.sectionId) this.open();
    }

    onSectionDeselect(event) {
      if (event.detail && event.detail.sectionId === this.sectionId) this.close();
    }

    onPageShow(event) {
      if (event.persisted) this.refresh();
    }

    /* ---------- Open / close ---------- */

    open(trigger) {
      if (this.classList.contains('is-open')) return;

      this.lastFocus = trigger || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('hs-cart-is-open');

      window.requestAnimationFrame(() => {
        const closeButton = this.querySelector('[data-hs-cart-close]');
        (closeButton || this.panel).focus();
      });
    }

    close() {
      if (!this.classList.contains('is-open')) return;

      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('hs-cart-is-open');

      if (this.lastFocus && this.lastFocus.isConnected && typeof this.lastFocus.focus === 'function') {
        this.lastFocus.focus();
      }
    }

    /* ---------- Cart actions ---------- */

    async add(formData, trigger) {
      if (this.busy) return;

      this.setBusy(true, trigger);
      this.clearError();

      formData.append('sections', this.getSectionIds().join(','));
      formData.append('sections_url', window.location.pathname);

      try {
        const response = await fetch(this.addUrl, {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/javascript'
          },
          body: formData
        });
        const data = await response.json().catch(() => ({}));

        if (!response.ok || data.status) {
          this.showError(data.description || data.message || this.dataset.errorText);
          this.open(trigger);
          return;
        }

        this.renderSections(data.sections);
        this.announce(this.dataset.addedText);
        document.dispatchEvent(new CustomEvent('hs:cart:added', { detail: { item: data } }));

        if (this.openOnAdd) this.open(trigger);
      } catch (error) {
        this.showError(this.dataset.errorText);
        this.open(trigger);
      } finally {
        this.setBusy(false, trigger);
      }
    }

    async change(key, quantity) {
      if (this.busy || !key) return;

      this.setBusy(true);
      this.clearError();

      try {
        const response = await fetch(this.changeUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            id: key,
            quantity: quantity,
            sections: this.getSectionIds(),
            sections_url: window.location.pathname
          })
        });
        const data = await response.json().catch(() => ({}));

        if (!response.ok || data.status) {
          this.showError(data.description || data.message || this.dataset.errorText);
          return;
        }

        this.renderSections(data.sections);
        this.announce(this.dataset.updatedText);
      } catch (error) {
        this.showError(this.dataset.errorText);
      } finally {
        this.setBusy(false);
      }
    }

    async refresh() {
      try {
        const ids = this.getSectionIds().join(',');
        const response = await fetch(window.location.pathname + '?sections=' + encodeURIComponent(ids));
        if (!response.ok) return;
        const data = await response.json();
        this.renderSections(data);
      } catch (error) {
        /* silent: drawer keeps its current content */
      }
    }

    /* ---------- Rendering ---------- */

    renderSections(sections) {
      if (!sections) return;

      const parser = new DOMParser();

      Object.keys(sections).forEach((id) => {
        const html = sections[id];
        if (!html) return;

        const doc = parser.parseFromString(html, 'text/html');

        if (id === this.sectionId) {
          const fresh = doc.querySelector('[data-hs-cart-content]');
          const current = this.querySelector('[data-hs-cart-content]');
          if (!fresh || !current) return;

          const focusInfo = this.captureFocus();
          current.replaceWith(fresh);
          this.restoreFocus(focusInfo);
          this.updateCount(fresh.dataset.itemCount);
          return;
        }

        const wrapper = document.getElementById('shopify-section-' + id);
        if (!wrapper) return;

        const currentLinks = this.getCartLinks(wrapper);
        const freshLinks = Array.from(doc.querySelectorAll('a[href]')).filter((link) => this.isCartLink(link));

        currentLinks.forEach((link, index) => {
          if (freshLinks[index]) link.innerHTML = freshLinks[index].innerHTML;
        });
      });
    }

    updateCount(count) {
      const itemCount = parseInt(count, 10) || 0;

      document.querySelectorAll('[data-hs-cart-count]').forEach((el) => {
        el.textContent = itemCount;
        el.hidden = itemCount === 0;
      });

      document.dispatchEvent(new CustomEvent('hs:cart:updated', { detail: { itemCount: itemCount } }));
    }

    captureFocus() {
      const el = document.activeElement;
      if (!el || !this.contains(el)) return null;
      return {
        action: el.dataset ? el.dataset.hsCartQty : null,
        key: el.dataset ? el.dataset.key : null
      };
    }

    restoreFocus(info) {
      if (!info) return;

      if (info.action && info.key && window.CSS && CSS.escape) {
        const target = this.querySelector(
          '[data-hs-cart-qty="' + CSS.escape(info.action) + '"][data-key="' + CSS.escape(info.key) + '"]'
        );
        if (target && !target.disabled) {
          target.focus();
          return;
        }
      }

      this.panel.focus();
    }

    /* ---------- UI state ---------- */

    setBusy(state, trigger) {
      this.busy = state;
      this.classList.toggle('is-busy', state);

      if (trigger && trigger.classList) {
        trigger.classList.toggle('hs-is-loading', state);
        trigger.setAttribute('aria-busy', state ? 'true' : 'false');
      }
    }

    showError(message) {
      if (!this.errorBox) return;
      this.errorBox.textContent = message || this.dataset.errorText || '';
      this.errorBox.hidden = false;
      this.announce(this.errorBox.textContent);
    }

    clearError() {
      if (!this.errorBox) return;
      this.errorBox.textContent = '';
      this.errorBox.hidden = true;
    }

    announce(text) {
      if (!this.live || !text) return;
      this.live.textContent = '';
      window.setTimeout(() => {
        this.live.textContent = text;
      }, 60);
    }
  }

  customElements.define('hs-cart-drawer', HsCartDrawer);
})();